import {
  AUDIT_AEO_HREF,
  AUDIT_ARO_HREF,
  AUDIT_CRO_HREF,
  HOMEPAGE_PREVIEW_AEO_TITLE,
  HOMEPAGE_PREVIEW_ARO_TITLE,
  HOMEPAGE_PREVIEW_CRO_TITLE,
  HOMEPAGE_PREVIEW_SAMPLE_LABEL,
  RESULTS_AEO_LABEL,
  RESULTS_AGENTS_LABEL,
  RESULTS_NOT_VERIFIED,
  RESULTS_OPPORTUNITY,
  RESULTS_PEOPLE_LABEL,
} from "~/lib/siteglue/landing-redesign/copy";

export const HOMEPAGE_PREVIEW_INTERVAL_MS = 6000;

export type HomepagePreviewSlideId = "aeo" | "aro" | "cro";

export type HomepagePreviewRow = {
  id: string;
  label: string;
  score: number | null;
  result: "Verified" | typeof RESULTS_OPPORTUNITY | typeof RESULTS_NOT_VERIFIED;
};

export type HomepagePreviewSlide = {
  id: HomepagePreviewSlideId;
  kicker: string;
  href: string;
  title: string;
  chromeTitle: string;
  scoreLabel: string;
  score: number | null;
  summary: string;
  rows: readonly HomepagePreviewRow[];
};

const SLIDE_ORDER: readonly HomepagePreviewSlideId[] = ["aro", "aeo", "cro"];

export const OPTIMIZATION_SERVICE_TAB_ORDER: readonly HomepagePreviewSlideId[] = SLIDE_ORDER;

export const HOMEPAGE_PREVIEW_SLIDES: readonly HomepagePreviewSlide[] = [
  {
    id: "aro",
    kicker: HOMEPAGE_PREVIEW_ARO_TITLE,
    href: AUDIT_ARO_HREF,
    title: RESULTS_AGENTS_LABEL,
    chromeTitle: "ARO — Agent Readiness",
    scoreLabel: "ARO Score",
    score: 72,
    summary: "Discovery, structured knowledge, MCP connectivity, and agent actions.",
    rows: [
      { id: "discovery", label: "Discovery and crawlability", score: 88, result: "Verified" },
      { id: "knowledge", label: "Structured business knowledge", score: 76, result: "Verified" },
      { id: "mcp", label: "MCP and agent connectivity", score: 41, result: RESULTS_OPPORTUNITY },
      { id: "actions", label: "Agent-compatible tools and actions", score: 38, result: RESULTS_OPPORTUNITY },
    ],
  },
  {
    id: "aeo",
    kicker: HOMEPAGE_PREVIEW_AEO_TITLE,
    href: AUDIT_AEO_HREF,
    title: RESULTS_AEO_LABEL,
    chromeTitle: "AEO Optimization Report",
    scoreLabel: "AEO Score",
    score: null,
    summary: "Technical entity clarity from your site. Live ChatGPT, Gemini, and Perplexity recommendations stay Not verified until a labeled test runs.",
    rows: [
      { id: "entity", label: "Entity and content clarity", score: 68, result: "Verified" },
      { id: "visibility", label: "AI answer visibility", score: null, result: RESULTS_NOT_VERIFIED },
      { id: "mentions", label: "Brand mentions", score: null, result: RESULTS_NOT_VERIFIED },
      { id: "citations", label: "Citation quality", score: null, result: RESULTS_NOT_VERIFIED },
      { id: "share", label: "Competitive share of voice", score: null, result: RESULTS_NOT_VERIFIED },
      { id: "accuracy", label: "Answer accuracy", score: null, result: RESULTS_NOT_VERIFIED },
    ],
  },
  {
    id: "cro",
    kicker: HOMEPAGE_PREVIEW_CRO_TITLE,
    href: AUDIT_CRO_HREF,
    title: RESULTS_PEOPLE_LABEL,
    chromeTitle: "CRO — Conversion Optimization",
    scoreLabel: "CRO Score",
    score: 81,
    summary: "First impression, CTAs, trust, mobile, and conversion flow across your channels.",
    rows: [
      { id: "first", label: "First impression", score: 86, result: "Verified" },
      { id: "cta", label: "CTA effectiveness", score: 79, result: "Verified" },
      { id: "trust", label: "Trust signals", score: 74, result: "Verified" },
      { id: "flow", label: "Conversion flow", score: 41, result: RESULTS_OPPORTUNITY },
    ],
  },
] as const;

export function nextHomepagePreviewSlide(current: HomepagePreviewSlideId): HomepagePreviewSlideId {
  const index = SLIDE_ORDER.indexOf(current);
  return SLIDE_ORDER[(index + 1) % SLIDE_ORDER.length] ?? "aro";
}

export function previousHomepagePreviewSlide(current: HomepagePreviewSlideId): HomepagePreviewSlideId {
  const index = SLIDE_ORDER.indexOf(current);
  return SLIDE_ORDER[(index - 1 + SLIDE_ORDER.length) % SLIDE_ORDER.length] ?? "aro";
}

export function homepagePreviewShouldAutoplay(options: {
  reducedMotion: boolean;
  paused: boolean;
  pageVisible: boolean;
}): boolean {
  return !options.reducedMotion && !options.paused && options.pageVisible;
}

export function homepagePreviewSampleLabel(): string {
  return HOMEPAGE_PREVIEW_SAMPLE_LABEL;
}

export function homepagePreviewSlide(
  id: HomepagePreviewSlideId,
): HomepagePreviewSlide {
  return HOMEPAGE_PREVIEW_SLIDES.find((slide) => slide.id === id) ?? HOMEPAGE_PREVIEW_SLIDES[0]!;
}
